<template>
  <NewStudioModalsModalShell class="save-your-design-modal" title="Save Your Design?" size="compact" @close="$emit('close')">
    <p id="save-design-description" class="save-design-intro">Simply enter an email address to save all your designs in one place or continue without saving.</p>
    <form @submit.prevent="saveDesign">
      <label class="sr-only" for="save-design-email">Email Address</label>
      <input id="save-design-email" v-model.trim="email" type="email" placeholder="Enter your email" required autocomplete="email">
      <p v-if="error" role="alert">{{ error }}</p>
      <button type="submit" class="save-design-btn" aria-describedby="save-design-description" :disabled="working || !email">{{ working ? 'Saving…' : 'Save Design' }}</button>
      <button class="continue-btn" type="button" :disabled="working" @click="handleContinueWithoutSaving">Continue Without Saving</button>
    </form>
  </NewStudioModalsModalShell>
</template>

<script setup lang="ts">
import { ModalType } from '~/stores/modal'
import { completeNewStudioModal, continueWithoutSaving } from '../logic'

defineEmits<{ close: [] }>()

const userStore = useUserStore()
const modalStore = useModalStore()
const projectStore = useProjectStore()
const email = ref('')
const error = ref('')
const working = ref(false)

// Pre-fill with the user's email if already logged in
onMounted(() => {
  if (userStore.email) {
    email.value = userStore.email
  }
})

const saveDesign = async () => {
  error.value = ''
  working.value = true
  try {
    // If not logged in with this email, log in first
    if (!userStore.isLoggedIn || userStore.email !== email.value) {
      const success = await userStore.login(email.value)
      if (!success) {
        error.value = 'We could not log you in. Check your email and try again.'
        return
      }
    }
    // Save the design
    await projectStore.save(true, true)
    await completeNewStudioModal(modalStore, ModalType.SaveDesign)
  } catch {
    error.value = 'We could not save your design. Please try again.'
  } finally {
    working.value = false
  }
}

const handleContinueWithoutSaving = async () => {
  error.value = ''
  working.value = true
  try {
    await continueWithoutSaving(modalStore, ModalType.SaveDesign)
  } catch {
    error.value = 'We could not continue to the cart. Please try again.'
  } finally {
    working.value = false
  }
}
</script>

<style scoped>
.save-design-intro {
  margin: 17.95px 0 0;
  color: #55554f;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
}

form {
  display: grid;
  gap: 17.95px;
  margin-top: 17.95px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}

input {
  box-sizing: border-box;
  height: 50px;
  padding: 0 18px;
  border: 2px solid #d9d8d3;
  border-radius: 0;
  font: inherit;
  font-size: 17.95px;
  font-weight: 600;
}

p[role='alert'] {
  margin: 0;
  color: #a32121;
  font-size: 13px;
}

.save-design-btn {
  min-height: 52px;
  border: 0;
  background: #2e3a46;
  color: #fff;
  font: inherit;
  font-size: 16.155px;
  font-weight: 700;
  cursor: pointer;
}

.continue-btn {
  min-height: 52px;
  border: 2px solid #deddd9;
  background: transparent;
  color: #777671;
  font: inherit;
  font-size: 16.155px;
  font-weight: 700;
  cursor: pointer;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

:global(.save-your-design-modal > h2) {
  margin-top: 17.95px;
  text-align: left;
  font-size: 34.105px;
  letter-spacing: -0.341px;
}

:global(.save-your-design-modal) {
  box-sizing: border-box;
  min-height: 334px;
}
</style>
